"use client";

import { useEffect, useRef } from "react";

import { useAuth } from "@/components/providers/auth-provider";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { notificationsApi, notificationsApiHelpers } from "../api/notifications-api";
import { getNotificationInvalidateTags } from "../lib/notification-utils";
import {
  createNotificationsSocket,
  type NotificationsSocket,
} from "../lib/notifications-socket";
import {
  applyReadSync,
  hydrateNotifications,
  receiveNotification,
  resetNotifications,
  setSocketConnected,
  setUnreadCount,
} from "../state/notifications-slice";
import useSound from "use-sound";

const NOTIFICATIONS_PAGE_SIZE = 20;

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const { isHydrated, isAuthenticated, accessToken } = useAuth();
  const socketRef = useRef<NotificationsSocket | null>(null);
  const shouldEnableNotifications =
    isHydrated && isAuthenticated && Boolean(accessToken);

  const [playNotificationSound] = useSound("/audio/notification/n1.wav", {
    volume: 1.0,
    preload: true,
  });



  const notificationsQuery = notificationsApi.useGetNotificationsQuery(
    {
      limit: NOTIFICATIONS_PAGE_SIZE,
      skip: 0,
    },
    {
      skip: !shouldEnableNotifications,
      refetchOnMountOrArgChange: true,
    },
  );
  const unreadCountQuery = notificationsApi.useGetUnreadNotificationCountQuery(
    undefined,
    {
      skip: !shouldEnableNotifications,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (!notificationsQuery.data) {
      return;
    }

    dispatch(hydrateNotifications(notificationsQuery.data));
  }, [dispatch, notificationsQuery.data]);

  useEffect(() => {
    if (typeof unreadCountQuery.data !== "number") {
      return;
    }

    dispatch(setUnreadCount(unreadCountQuery.data));
  }, [dispatch, unreadCountQuery.data]);

  useEffect(() => {
    if (shouldEnableNotifications) {
      return;
    }

    socketRef.current?.disconnect();
    socketRef.current = null;
    dispatch(setSocketConnected(false));
    dispatch(resetNotifications());
  }, [dispatch, shouldEnableNotifications]);

  useEffect(() => {
    if (!shouldEnableNotifications || !accessToken) {
      return;
    }

    socketRef.current?.disconnect();

    const socket = createNotificationsSocket(accessToken);
    socketRef.current = socket;

    socket.on("connect", () => {
      dispatch(setSocketConnected(true));
    });

    socket.on("connect_error", () => {
      dispatch(setSocketConnected(false));
    });

    socket.on("disconnect", () => {
      dispatch(setSocketConnected(false));
    });

    socket.on("notification:new", (payload) => {
      const nextNotification = notificationsApiHelpers.toNotificationItem(payload);
      if (!nextNotification) {
        return;
      }

      const tagsToInvalidate = getNotificationInvalidateTags(
        nextNotification.entity_type,
        nextNotification.entity_id,
      );
      if (tagsToInvalidate?.length) {
        dispatch(baseApi.util.invalidateTags(tagsToInvalidate));
      }

      dispatch(receiveNotification(nextNotification));
      playNotificationSound();
    });

    socket.on("notification:unread_count", (payload) => {
      const unreadCount = notificationsApiHelpers.extractUnreadCountEvent(payload);
      if (typeof unreadCount === "number") {
        dispatch(setUnreadCount(unreadCount));
      }
    });

    socket.on("notification:read_sync", (payload) => {
      const readSyncEvent = notificationsApiHelpers.extractReadSyncEvent(payload);
      if (!readSyncEvent) {
        return;
      }

      dispatch(applyReadSync(readSyncEvent));
    });

    socket.connect();

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      dispatch(setSocketConnected(false));
    };
  }, [accessToken, dispatch, shouldEnableNotifications, playNotificationSound]);

  return children;
}
